// src/components/Header.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiMenu, HiX, HiArrowRight } from 'react-icons/hi';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/efa_logo.png';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // --- START: ADDED CAREERS ITEM HERE ---
  const navItems = [
    { name: 'Home', href: '#home', type: 'scroll' },
    { name: 'Services', href: '#services', type: 'scroll' },
    { name: 'About', href: '#about', type: 'scroll' },
    { name: 'Projects', href: '#projects', type: 'scroll' },
    { name: 'Service Partners', href: '#partners', type: 'scroll' },
    { name: 'Testimonials', href: '#testimonials', type: 'scroll' },
    // ADDED THE NEW CAREERS LINK
    { name: 'Careers', href: '/careers', type: 'link' }, 
  ];
  // --- END: ADDED CAREERS ITEM HERE ---

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href, type) => {
    setIsOpen(false);

    // If it's a standard page link, use React Router to navigate
    if (type === 'link') {
        navigate(href);
        return; 
    }
    
    // Special handling for scroll links - navigate to home page first if not already there
    if (location.pathname !== '/') {
        navigate('/' + href);
        return;
    }
    
    // If it's a scroll link (existing functionality) - only works on home page
    setTimeout(() => {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  const handleGetStartedClick = () => {
    setIsOpen(false);
    navigate('/contact');
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-lg shadow-md py-1' : 'bg-white/90 backdrop-blur-sm py-1.5'
      }`}
    >
      <div className="container-custom px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex items-center space-x-2"
          >
            <Link to="/" className="flex items-center space-x-2">
              <img src={logo} alt="EFA Tech Solutions Logo" className="w-8 h-8" />
              <span className={`text-base font-poppins font-bold text-neutral-900`}>
                EFA Tech Solutions
              </span>
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <nav className="flex space-x-6">
              {navItems.map((item) => (
                // --- START: UPDATED LINK LOGIC HERE ---
                <motion.a
                  key={item.name}
                  href={item.href}
                  whileHover={{ y: -2 }}
                  onClick={(e) => {
                    if (item.type === 'scroll') {
                      e.preventDefault();
                    }
                    handleNavClick(item.href, item.type); // Pass the type
                  }}
                  className={`transition-colors duration-300 font-poppins cursor-pointer text-neutral-900 hover:text-primary-500 text-sm`}
                >
                  {item.name}
                </motion.a>
                // --- END: UPDATED LINK LOGIC HERE ---
              ))}
            </nav>

            {/* Enhanced Get Started Button */}
            <motion.button
              whileHover={{
                scale: 1.05,
                boxShadow: "0 10px 25px rgba(0, 123, 255, 0.3)"
              }}
              whileTap={{ scale: 0.95 }}
              onClick={handleGetStartedClick}
              className="relative overflow-hidden bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white px-5 py-2 rounded-lg font-semibold transition-all duration-300 group"
            >
              {/* Animated Background */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-primary-400 to-primary-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                animate={{
                  x: [-100, 100],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />

              {/* Button Content */}
              <span className="relative z-10 flex items-center space-x-2">
                <span>Get Started</span>
                <motion.div
                  animate={{
                    x: [0, 3, 0],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <HiArrowRight className="w-4 h-4" />
                </motion.div>
              </span>

              {/* Sparkle Effect */}
              <motion.div
                className="absolute top-1 right-2 w-1 h-1 bg-white rounded-full opacity-0"
                animate={{
                  opacity: [0, 1, 0],
                  scale: [0, 1, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </motion.button>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className="md:hidden z-50 p-3 rounded-lg hover:bg-neutral-100 transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            whileTap={{ scale: 0.95 }}
          >
            {isOpen ? (
              <HiX className={`w-6 h-6 text-neutral-900`} />
            ) : (
              <HiMenu className={`w-6 h-6 text-neutral-900`} />
            )}
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden mt-4 bg-white/95 backdrop-blur-lg rounded-2xl shadow-xl border border-neutral-100 overflow-hidden"
            >
              <div className="py-4">
                {navItems.map((item) => (
                  // --- START: UPDATED MOBILE LINK LOGIC HERE ---
                  <motion.button
                    key={item.name}
                    whileHover={{ x: 10 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleNavClick(item.href, item.type)} // Pass the type
                    className="w-full text-left block px-6 py-4 text-neutral-900 hover:text-primary-600 hover:bg-neutral-50 active:bg-neutral-100 transition-all duration-300 font-medium text-base"
                  >
                    {item.name}
                  </motion.button>
                  // --- END: UPDATED MOBILE LINK LOGIC HERE ---
                ))}

                {/* Mobile Get Started Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleGetStartedClick}
                  className="w-full mx-6 mt-4 mb-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-6 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
                >
                  <span>Get Started</span>
                  <HiArrowRight className="w-5 h-5" />
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;
